<template>
  <!--
    WB-M2A S2 (doc 31 §9, §12.7) -- the share sheet of Board → AI Tutor برنا.

    The Student sees EXACTLY the picture that will be sent (the frozen
    snapshot the Workspace rendered when the sheet opened), what it is of
    («۳ مورد انتخاب‌شده» or «نمای فعلی تخته»), and that the Tutor sees this
    picture and not the live board. Underneath: two optional kinds of help
    («راهنمایی بده», «این قسمت را توضیح بده»), an optional question, and the
    message that will travel with the picture -- always visible, never a
    hidden prompt: the Student's own words when they wrote any, else the
    default text of the chosen help, else a generic default. A turn with a
    picture and no text is refused upstream (doc 31 §12.2), so the text is
    never empty.

    The sheet is modal: a backdrop over the whole page, focus kept inside,
    Escape and the backdrop cancel. While it is open nothing can reach the
    board, so the preview stays the authority for what is sent. It owns the
    draft question and the chosen help, nothing else: the picture is the
    Workspace's frozen share, and sending is the Workspace's.

    The dialog shape is the board's own Equation editor's (centred; a sheet
    at the bottom on a phone).
  -->
  <div
    class="ai-tutor-share-dialog__backdrop"
    data-testid="ai-tutor-board-share-dialog"
    :data-scope="share ? share.scope : ''"
    :style="viewportStyle"
    @mousedown="pressedOutside = $event.target === $event.currentTarget"
    @click.self="onBackdropClick"
  >
    <section
      ref="dialog"
      class="ai-tutor-share-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ai-tutor-share-dialog-title"
      tabindex="-1"
    >
      <h2 id="ai-tutor-share-dialog-title" class="ai-tutor-share-dialog__title">
        {{ $t('aiTutorBoard.share.title') }}
      </h2>

      <template v-if="share">
        <!-- The frozen picture, exactly as it will be sent. -->
        <figure class="ai-tutor-share-dialog__preview">
          <img
            :src="share.previewUrl"
            :alt="$t('aiTutorBoard.share.previewAlt')"
            data-testid="ai-tutor-board-share-preview"
            :width="share.width || null"
            :height="share.height || null"
          />
          <figcaption>
            <span
              class="ai-tutor-share-dialog__scope"
              data-testid="ai-tutor-board-share-scope"
            >
              <board-icon
                :name="share.scope === 'selection' ? 'selection' : 'resetView'"
                :size="14"
              />
              {{ scopeLabel }}
            </span>
            <span
              class="ai-tutor-share-dialog__size"
              data-testid="ai-tutor-board-share-size"
            >
              {{ $t('aiTutorBoard.share.size', { size: sizeLabel }) }}
            </span>
          </figcaption>
        </figure>
        <p
          class="ai-tutor-share-dialog__privacy"
          data-testid="ai-tutor-board-share-privacy"
        >
          {{ $t('aiTutorBoard.share.privacy') }}
        </p>
        <p
          v-if="share.overSoftLimit"
          class="ai-tutor-share-dialog__note"
          data-testid="ai-tutor-board-share-large"
        >
          {{ $t('aiTutorBoard.share.large') }}
        </p>

        <!-- The two kinds of help: optional, one at most, toggled. -->
        <div
          class="ai-tutor-share-dialog__intents"
          role="group"
          :aria-label="$t('aiTutorBoard.share.intentLabel')"
        >
          <span class="ai-tutor-share-dialog__label">
            {{ $t('aiTutorBoard.share.intentLabel') }}
          </span>
          <div class="ai-tutor-share-dialog__chips">
            <button
              v-for="item in intents"
              :key="item"
              type="button"
              class="ai-tutor-share-dialog__chip"
              :class="{ 'ai-tutor-share-dialog__chip--on': intent === item }"
              :data-testid="`ai-tutor-board-share-intent-${item}`"
              :aria-pressed="intent === item ? 'true' : 'false'"
              :disabled="busy"
              @click="toggleIntent(item)"
            >
              {{ $t(`aiTutorBoard.share.intents.${item}`) }}
            </button>
          </div>
        </div>

        <label
          class="ai-tutor-share-dialog__label"
          for="ai-tutor-board-share-question"
        >
          {{ $t('aiTutorBoard.share.questionLabel') }}
        </label>
        <textarea
          id="ai-tutor-board-share-question"
          ref="question"
          v-model="question"
          class="ai-tutor-share-dialog__question"
          data-testid="ai-tutor-board-share-question"
          rows="2"
          :maxlength="questionMax"
          :placeholder="$t('aiTutorBoard.share.questionPlaceholder')"
          :disabled="busy"
        ></textarea>
        <!-- What travels with the picture, when the Student wrote nothing. -->
        <p
          v-if="!typed"
          class="ai-tutor-share-dialog__will-send"
          data-testid="ai-tutor-board-share-will-send"
        >
          <span>{{ $t('aiTutorBoard.share.willSend') }}</span>
          <q>{{ messageText }}</q>
        </p>
        <p class="ai-tutor-share-dialog__limitation">
          {{ $t('aiTutorBoard.share.limitation') }}
        </p>
      </template>

      <div
        class="ai-tutor-share-dialog__status"
        role="status"
        aria-live="polite"
      >
        <p
          v-if="message"
          class="ai-tutor-share-dialog__message"
          data-testid="ai-tutor-board-share-message"
          :data-problem="problemKey"
        >
          {{ message }}
        </p>
      </div>

      <div class="ai-tutor-share-dialog__actions">
        <button
          type="button"
          class="ai-tutor-share-dialog__primary"
          data-testid="ai-tutor-board-share-send"
          :disabled="!canSend"
          @click="onSend"
        >
          {{
            busy
              ? $t('aiTutorBoard.share.sending')
              : $t('aiTutorBoard.share.send')
          }}
        </button>
        <button
          type="button"
          class="ai-tutor-share-dialog__secondary"
          data-testid="ai-tutor-board-share-cancel"
          :disabled="busy"
          @click="$emit('cancel')"
        >
          {{ $t('aiTutorBoard.share.cancel') }}
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import BoardIcon from './BoardIcon.vue'
import {
  BOARD_SHARE_INTENTS,
  boardShareMessage,
} from '@/services/aiTutor/boardShare'

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

// The upstream caps a turn's text at 4000 characters (doc 31 §3.2).
const QUESTION_MAX = 2000

const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹'

function localDigits(value, locale) {
  const text = String(value)
  if (locale !== 'fa') return text
  return text.replace(/\d/g, digit => PERSIAN_DIGITS[Number(digit)])
}

export default {
  name: 'BoardShareDialog',
  components: { BoardIcon },
  props: {
    // The frozen share (`boardSnapshot.freezeBoardShare`): previewUrl,
    // scope, selectedCount, width, height, bytes, overSoftLimit. Null while
    // there is nothing to share (then only `problem` is shown).
    share: { type: Object, default: null },
    // The Workspace is sending it.
    busy: { type: Boolean, default: false },
    // Why it cannot be sent, or could not be: 'empty' | 'render' | 'stale'
    // | 'readOnly' | 'chat' | 'credits' | 'busy'. Shown above the actions.
    problem: { type: String, default: null },
  },
  data() {
    return {
      question: '',
      // null | 'hint' | 'explain'
      intent: null,
      viewport: null,
      pressedOutside: false,
    }
  },
  computed: {
    intents() {
      return BOARD_SHARE_INTENTS
    },
    questionMax() {
      return QUESTION_MAX
    },
    locale() {
      return (this.$i18n && this.$i18n.locale) || 'fa'
    },
    typed() {
      return this.question.trim().length > 0
    },
    // Exactly what will be sent as the Student's message.
    messageText() {
      return boardShareMessage({
        question: this.question,
        intent: this.intent,
        defaults: {
          generic: this.$t('aiTutorBoard.share.defaults.generic'),
          hint: this.$t('aiTutorBoard.share.defaults.hint'),
          explain: this.$t('aiTutorBoard.share.defaults.explain'),
        },
      })
    },
    scopeLabel() {
      if (!this.share) return ''
      if (this.share.scope === 'selection') {
        return this.$t('aiTutorBoard.share.scopeSelection', {
          count: localDigits(this.share.selectedCount || 1, this.locale),
        })
      }
      return this.$t('aiTutorBoard.share.scopeViewport')
    },
    sizeLabel() {
      const bytes = (this.share && this.share.bytes) || 0
      const value =
        bytes >= 1024 * 1024
          ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
          : `${Math.max(1, Math.round(bytes / 1024))} KB`
      return localDigits(value, this.locale)
    },
    problemKey() {
      return this.problem || ''
    },
    message() {
      return this.problem
        ? this.$t(`aiTutorBoard.share.problems.${this.problem}`)
        : ''
    },
    // A problem that is about THIS moment (the chat, the credits, a send in
    // flight, a stale board) blocks the send; 'empty' / 'render' have
    // nothing to send at all.
    canSend() {
      return !!this.share && !this.busy && !this.problem
    },
    viewportStyle() {
      const viewport = this.viewport
      return viewport
        ? { top: `${viewport.top}px`, height: `${viewport.height}px` }
        : null
    },
  },
  mounted() {
    this.previouslyFocused =
      document.activeElement && document.activeElement !== document.body
        ? document.activeElement
        : null
    document.addEventListener('keydown', this.onKeydown)
    this.previousBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.onViewport)
      window.visualViewport.addEventListener('scroll', this.onViewport)
      this.onViewport()
    }
    if (this.$refs.dialog) this.$refs.dialog.focus()
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown)
    document.body.style.overflow = this.previousBodyOverflow || ''
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.onViewport)
      window.visualViewport.removeEventListener('scroll', this.onViewport)
    }
    const target = this.previouslyFocused
    if (target && typeof target.focus === 'function' && target.isConnected) {
      target.focus()
    }
  },
  methods: {
    toggleIntent(item) {
      this.intent = this.intent === item ? null : item
    },
    onSend() {
      if (!this.canSend) return
      this.$emit('send', {
        text: this.messageText,
        intent: this.intent,
        question: this.question.trim(),
      })
    },
    onBackdropClick() {
      if (this.pressedOutside && !this.busy) this.$emit('cancel')
      this.pressedOutside = false
    },
    onViewport() {
      const viewport = window.visualViewport
      if (!viewport) return
      this.viewport = { top: viewport.offsetTop, height: viewport.height }
    },
    onKeydown(e) {
      if (e.key === 'Escape') {
        if (!this.busy) this.$emit('cancel')
        return
      }
      if (e.key === 'Tab') this.trapFocus(e)
    },
    trapFocus(e) {
      const root = this.$refs.dialog
      if (!root) return
      const focusable = Array.from(root.querySelectorAll(FOCUSABLE))
      if (focusable.length === 0) {
        e.preventDefault()
        root.focus()
        return
      }
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement
      const inside = root.contains(active)
      if (e.shiftKey) {
        if (!inside || active === first || active === root) {
          e.preventDefault()
          last.focus()
        }
      } else if (!inside || active === last) {
        e.preventDefault()
        first.focus()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.ai-tutor-share-dialog__backdrop {
  position: fixed;
  inset: 0;
  // The Equation editor's layer: above the shell, the chat sheet and the
  // sidebar.
  z-index: 160;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 20px;
  background: rgba(7, 20, 71, 0.38);
  font-family: $borna-font-family;
}

.ai-tutor-share-dialog {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: min(100%, 560px);
  max-height: 100%;
  padding: 20px 22px 22px;
  overflow-x: hidden;
  overflow-y: auto;
  border-radius: 24px;
  outline: none;
  background: $borna-surface;
  box-shadow: 0 22px 64px rgba(7, 20, 71, 0.2);
  color: $borna-text;
  font-size: 13px;
  line-height: 1.8;
}

.ai-tutor-share-dialog__title {
  margin: 0 0 12px;
  color: $borna-ink;
  font-size: 17px;
  font-weight: $borna-fw-medium;
  line-height: 1.6;
}

// The picture, on the paper it will be sent on, never larger than the
// sheet and never taller than a third of the screen.
.ai-tutor-share-dialog__preview {
  flex: none;
  margin: 0;
  overflow: hidden;
  border: 1px solid $borna-border;
  border-radius: 14px;
  background: #fff;

  img {
    display: block;
    width: auto;
    max-width: 100%;
    height: auto;
    max-height: min(34vh, 320px);
    margin: 0 auto;
    object-fit: contain;
  }

  figcaption {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 6px 12px;
    padding: 7px 10px;
    border-top: 1px solid $borna-border;
    background: $borna-canvas;
    color: $borna-muted;
    font-size: 11px;
  }
}

.ai-tutor-share-dialog__scope {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: $borna-indigo-900;
  font-weight: $borna-fw-bold;
}

.ai-tutor-share-dialog__privacy {
  margin: 10px 0 0;
  color: $borna-text;
  font-size: 12px;
}

.ai-tutor-share-dialog__note {
  margin: 4px 0 0;
  color: $borna-muted;
  font-size: 12px;
}

.ai-tutor-share-dialog__label {
  display: block;
  margin: 12px 0 6px;
  color: $borna-muted;
  font-size: 12px;
  font-weight: $borna-fw-bold;
}

.ai-tutor-share-dialog__intents .ai-tutor-share-dialog__label {
  margin-top: 14px;
}

.ai-tutor-share-dialog__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.ai-tutor-share-dialog__chip {
  min-height: 36px;
  padding: 0 14px;
  border: 1px solid $borna-border;
  border-radius: $borna-radius-pill;
  background: $borna-canvas;
  color: $borna-text;
  font-family: inherit;
  font-size: 12px;
  font-weight: $borna-fw-bold;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    border-color: $borna-indigo-300;
    background: $borna-indigo-50;
  }

  &--on,
  &--on:hover {
    border-color: $borna-indigo-300;
    background: $borna-indigo-50;
    color: $borna-indigo-900;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}

.ai-tutor-share-dialog__question {
  box-sizing: border-box;
  width: 100%;
  min-height: 60px;
  padding: 9px 12px;
  border: 1px solid $borna-border;
  border-radius: 13px;
  background: $borna-surface;
  color: $borna-ink;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.8;
  resize: vertical;

  &:focus {
    border-color: $borna-indigo-300;
    outline: 2px solid $borna-indigo-100;
    outline-offset: 0;
  }

  &:disabled {
    opacity: 0.6;
  }
}

.ai-tutor-share-dialog__will-send {
  margin: 6px 0 0;
  color: $borna-muted;
  font-size: 12px;

  span {
    margin-inline-end: 4px;
  }

  q {
    color: $borna-ink;
    quotes: '«' '»';
  }
}

.ai-tutor-share-dialog__limitation {
  margin: 10px 0 0;
  color: $borna-muted;
  font-size: 11px;
}

.ai-tutor-share-dialog__status {
  display: flex;
  flex: none;
  align-items: center;
  min-height: 16px;
  margin: 8px 0;
}

.ai-tutor-share-dialog__message {
  flex: 1;
  margin: 0;
  color: $borna-error-text;
  font-size: 13px;
  line-height: 1.8;
}

.ai-tutor-share-dialog__actions {
  display: grid;
  flex: none;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px;
}

.ai-tutor-share-dialog__primary,
.ai-tutor-share-dialog__secondary {
  min-height: 44px;
  padding: 0 12px;
  border-radius: 13px;
  font-family: inherit;
  font-size: 13px;
  font-weight: $borna-fw-extrabold;
  cursor: pointer;
}

.ai-tutor-share-dialog__primary {
  border: 1px solid transparent;
  background: $borna-indigo-600;
  color: #fff;

  &:hover,
  &:focus-visible {
    background: $borna-indigo-900;
  }

  &:disabled {
    background: $borna-indigo-100;
    color: $borna-indigo-600;
    cursor: not-allowed;
  }
}

.ai-tutor-share-dialog__secondary {
  border: 1px solid $borna-border;
  background: $borna-surface;
  color: $borna-text;

  &:hover,
  &:focus-visible {
    background: $borna-canvas;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}

.ai-tutor-share-dialog button:focus-visible {
  outline: 2px solid $borna-indigo-600;
  outline-offset: 3px;
}

// The phone tier: a sheet at the bottom of the visible viewport.
@include borna-phone {
  .ai-tutor-share-dialog__backdrop {
    align-items: flex-end;
    padding: 12px;
  }

  .ai-tutor-share-dialog {
    width: 100%;
    padding: 16px;
    border-radius: 24px 24px 18px 18px;
  }

  .ai-tutor-share-dialog__preview img {
    max-height: 28vh;
  }
}
</style>
